<script>
    export default {
        name: "Pagination",
        props: {
            count: {
                type: Number,
                required: true
            },
            defaultLimit: {
                type: Number,
                required: true
            },
            disabled: Boolean,
        },
        data() {

            return {
                page: 1,
                limit: this.defaultLimit,
            };
        },
        computed: {
            total() {
                return Math.max(1, Math.ceil(this.count / this.limit));
            },
            pageItems() {
                const items = [];
                const total = this.total;
                const current = this.page;

                // Prev
                items.push({
                    type: "prev",
                    label: "‹",
                    page: Math.max(1, current - 1),
                    disabled: current === 1,
                });

                if (total <= 8) {
                    // Show all pages
                    for (let i = 1; i <= total; i++) {
                        items.push({
                            type: "page",
                            label: String(i),
                            page: i,
                            active: i === current,
                        });
                    }
                } else {
                    // Always show first
                    items.push({
                        type: "page",
                        label: "1",
                        page: 1,
                        active: current === 1,
                    });

                    // Window around current: [current-2 .. current+2]
                    const start = Math.max(2, current - 2);
                    const end = Math.min(total - 1, current + 2);

                    // Left ellipsis if there's a gap from 1 to start
                    if (start > 2) {
                        items.push({type: "ellipsis", label: "…"});
                    }

                    // Window pages
                    for (let i = start; i <= end; i++) {
                        items.push({
                            type: "page",
                            label: String(i),
                            page: i,
                            active: i === current,
                        });
                    }

                    // Right ellipsis if there's a gap from end to last
                    if (end < total - 1) {
                        items.push({type: "ellipsis", label: "…"});
                    }

                    // Always show last
                    items.push({
                        type: "page",
                        label: String(total),
                        page: total,
                        active: current === total,
                    });
                }

                // Next
                items.push({
                    type: "next",
                    label: "›",
                    page: Math.min(total, current + 1),
                    disabled: current === total,
                });

                return items;
            },
        },
        methods: {
            select(page) {
                if (this.disabled) return;

                const clamped = page < 1 ? 1 : page > this.total ? this.total : parseInt(page, 10);

                if (clamped === this.page) return;

                this.page = clamped;
                this.refresh();
            },
            refresh() {
                const pageIdx = this.page === 0 ? 0 : this.page - 1;
                const offset = pageIdx * this.limit;

                this.$emit("refresh", {
                    offset,
                    limit: this.limit,
                    total: this.total,
                    page: this.page,
                });
            },
        },
        watch: {
            // If total changes (e.g., count or limit changes) and current page is out of range, fix it.
            total(newTotal) {
                if (this.page > newTotal) {
                    this.page = newTotal;
                    this.refresh();
                }
            },
            defaultLimit(newVal) {
                // Keep limit in sync if parent changes prop
                if (typeof newVal === "number" && newVal > 0) {
                    this.limit = newVal;
                    // Optionally reset to page 1 when limit changes
                    if (this.page !== 1) {
                        this.page = 1;
                    }
                    this.refresh();
                }
            },
            count() {
                // When count changes, refresh to emit new totals/offset if needed
                this.refresh();
            },
        },
    };
</script>

<template>
    <nav v-if="total > 1">
        <ul class="pagination mb-0">
            <li
                v-for="(it, idx) in pageItems"
                :key="idx + '-' + it.type + '-' + (it.page || it.label)"
                class="page-item"
                :class="{active: it.type === 'page' && it.active, disabled: disabled || it.type === 'ellipsis' || it.disabled}">
                <a v-if="it.type === 'page'"
                   class="page-link"
                   href="#"
                   role="button"
                   @click.prevent="select(it.page)">
                    {{it.label}}
                </a>

                <a v-else-if="it.type === 'prev' || it.type === 'next'"
                   class="page-link d-none d-sm-block"
                   href="#"
                   role="button"
                   :aria-label="it.type === 'prev' ? 'Previous' : 'Next'"
                   @click.prevent="!(disabled || it.disabled) && select(it.page)">
                    <span aria-hidden="true">{{it.label}}</span>
                </a>

                <span v-else class="page-link">{{it.label}}</span>
            </li>
        </ul>
    </nav>
</template>

<style scoped lang="scss">

</style>